import React, { useEffect, useMemo } from 'react';
import { View, ViewStyle, StyleProp } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withRepeat,
  withSequence,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import { useTheme } from '../../hooks/useTheme';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

export interface ProgressDotsProps {
  /** Size of the dots in pixels. Defaults to 20. */
  size?: number;
  /** The color to render the dots in. */
  color?: string;
  /** Additional style to apply to the container. */
  containerStyle?: StyleProp<ViewStyle>;
}

const useDotAnimation = (delay: number) => {
  const cy = useSharedValue(12);

  useEffect(() => {
    cy.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(6, {
            duration: 300,
            easing: Easing.bezier(0.33, 0.66, 0.66, 1),
          }),
          withTiming(12, {
            duration: 300,
            easing: Easing.bezier(0.33, 0, 0.66, 0.33),
          }),
        ),
        -1,
        false,
      ),
    );
  }, [cy, delay]);

  return useAnimatedProps(() => ({ cy: cy.value }), [cy]);
};

export const ProgressDots: React.FC<ProgressDotsProps> = ({
  size = 20,
  color,
  containerStyle,
}) => {
  const { colors } = useTheme();

  const sizeStyle = useMemo(() => ({ width: size, height: size }), [size]);

  const dot1 = useDotAnimation(0);
  const dot2 = useDotAnimation(100);
  const dot3 = useDotAnimation(200);

  return (
    <View style={[sizeStyle, containerStyle]}>
      <Svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={color ?? colors.primary}
      >
        <AnimatedCircle cx="4" cy="12" r="3" animatedProps={dot1} />
        <AnimatedCircle cx="12" cy="12" r="3" animatedProps={dot2} />
        <AnimatedCircle cx="20" cy="12" r="3" animatedProps={dot3} />
      </Svg>
    </View>
  );
};
